import React from 'react';

/**
 * Component for managing training zones in a more visually appealing way
 * @param {Object} props - Component props
 * @param {Object} props.value - Training zones data
 * @param {Function} props.onChange - Function to call when data changes
 * @param {boolean} props.disabled - Whether the component is disabled
 */
const TrainingZonesSection = ({ value = {}, onChange, disabled = false }) => {
  // Handle field change
  const handleFieldChange = (field, newValue) => {
    onChange({
      ...value,
      [field]: newValue
    });
  };

  // Zone colors for visual appeal
  const zoneColors = {
    zone1: '#6ec8eb', // Light blue
    zone2: '#92d050', // Light green
    zone3: '#ffff00', // Yellow
    zone4: '#ff9900', // Orange
    zone5: '#ff0000', // Red
  };

  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium mb-4">Training Zones</h3>
      
      {/* Recovery section */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium mb-3">Recovery Metrics</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recovery After 1 Min (BPM)
            </label>
            <input
              type="number"
              value={value.recovery_1min || ''}
              onChange={(e) => handleFieldChange('recovery_1min', e.target.value)}
              disabled={disabled}
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recovery After 2 Min (BPM)
            </label>
            <input
              type="number"
              value={value.recovery_2min || ''}
              onChange={(e) => handleFieldChange('recovery_2min', e.target.value)}
              disabled={disabled}
              className="w-full p-2 border rounded"
            />
          </div>
        </div>
      </div>
      
      {/* Training zones section */}
      <div className="mb-4">
        <h4 className="font-medium mb-3">Heart Rate Training Zones</h4>
        <p className="text-sm text-gray-500 mb-4">Enter BPM values for each training zone</p>
        
        <div className="space-y-4">
          {/* Zone 1 */}
          <div 
            className="p-4 rounded-lg border-l-4" 
            style={{ borderLeftColor: zoneColors.zone1, backgroundColor: `${zoneColors.zone1}20` }}
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="md:w-1/3">
                <label className="block text-sm font-medium text-gray-700">Zone 1</label>
                <div className="font-bold">{value.zone1_name || 'RECOVERY MODE'}</div>
              </div>
              <div className="md:w-2/3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BPM Range
                </label>
                <input
                  type="text"
                  value={value.zone1_bpm || ''}
                  onChange={(e) => handleFieldChange('zone1_bpm', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g. <138 BPM"
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
          </div>
          
          {/* Zone 2 */}
          <div 
            className="p-4 rounded-lg border-l-4" 
            style={{ borderLeftColor: zoneColors.zone2, backgroundColor: `${zoneColors.zone2}20` }}
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="md:w-1/3">
                <label className="block text-sm font-medium text-gray-700">Zone 2</label>
                <div className="font-bold">{value.zone2_name || 'AEROBIC BASE'}</div>
              </div>
              <div className="md:w-2/3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BPM Range
                </label>
                <input
                  type="text"
                  value={value.zone2_bpm || ''}
                  onChange={(e) => handleFieldChange('zone2_bpm', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g. 138-164 BPM"
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
          </div>
          
          {/* Zone 3 */}
          <div 
            className="p-4 rounded-lg border-l-4" 
            style={{ borderLeftColor: zoneColors.zone3, backgroundColor: `${zoneColors.zone3}20` }}
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="md:w-1/3">
                <label className="block text-sm font-medium text-gray-700">Zone 3</label>
                <div className="font-bold">{value.zone3_name || 'AEROBIC CAPACITY'}</div>
              </div>
              <div className="md:w-2/3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BPM Range
                </label>
                <input
                  type="text"
                  value={value.zone3_bpm || ''}
                  onChange={(e) => handleFieldChange('zone3_bpm', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g. 164-175 BPM"
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
          </div>
          
          {/* Zone 4 */}
          <div 
            className="p-4 rounded-lg border-l-4" 
            style={{ borderLeftColor: zoneColors.zone4, backgroundColor: `${zoneColors.zone4}20` }}
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="md:w-1/3">
                <label className="block text-sm font-medium text-gray-700">Zone 4</label>
                <div className="font-bold">{value.zone4_name || 'THRESHOLD ZONE'}</div>
              </div>
              <div className="md:w-2/3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BPM Range
                </label>
                <input
                  type="text"
                  value={value.zone4_bpm || ''}
                  onChange={(e) => handleFieldChange('zone4_bpm', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g. 175-176 BPM"
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
          </div>
          
          {/* Zone 5 */}
          <div 
            className="p-4 rounded-lg border-l-4" 
            style={{ borderLeftColor: zoneColors.zone5, backgroundColor: `${zoneColors.zone5}20` }}
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="md:w-1/3">
                <label className="block text-sm font-medium text-gray-700">Zone 5</label>
                <div className="font-bold">{value.zone5_name || 'VO2 MAX ZONE'}</div>
              </div>
              <div className="md:w-2/3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BPM Range
                </label>
                <input
                  type="text"
                  value={value.zone5_bpm || ''}
                  onChange={(e) => handleFieldChange('zone5_bpm', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g. >176 BPM"
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-4 text-sm text-gray-500">
        <p>Note: Enter BPM ranges for each training zone. These will be displayed in the report.</p>
      </div>
    </div>
  );
};

export default TrainingZonesSection;
